/**
 * Scoping a logged-in session to the marketplace of the current domain (#1397).
 *
 * GET /user/account/getByJWT returns every account the user owns or collaborates on, across
 * all marketplaces. A user who belongs to two marketplaces (e.g. Villa Accadia and Kaete Club,
 * both Dan Hotels) got a dashboard that mixed them: on members.kaeteclub.com the calendar,
 * stats and sub-accounts were Villa's. On a white-label domain the session must only see the
 * accounts of that domain's marketplace.
 *
 * Kept free of imports so it runs under node:test.
 */

const marketplaceIdOf = (account) => {
    const { marketPlace } = account || {};
    if (!marketPlace) return null;
    return String(typeof marketPlace === 'object' ? marketPlace._id : marketPlace);
};

const hostOf = (url) => {
    try {
        return new URL(url).hostname.toLowerCase();
    } catch {
        return null;
    }
};

// Same host, or one is a subdomain of the other: members.kaeteclub.com vs the market.js key
// kaeteclub.com (what ?domain= previews on localhost), www.bambooclub.co.il vs bambooclub.co.il.
const hostsMatch = (a, b) => a === b || a.endsWith(`.${b}`) || b.endsWith(`.${a}`);

/**
 * @param {Array<object>} accounts - getByJWT accounts; `marketPlace` is populated with `baseAppURL`
 *   (or not, before the backend ships it — then nothing is scoped)
 * @param {string|null} hostname - the current marketplace hostname, or null on a non white-label
 *   host (nimi.co.il serves sellers of many marketplaces, so it must never narrow the session)
 * @returns {{ accounts: Array<object>, marketplaceId: string|null }} the accounts to use, and the
 *   marketplace they were narrowed to (null when left as is)
 */
export const scopeAccountsToHost = (accounts, hostname) => {
    const unscoped = { accounts, marketplaceId: null };
    if (!Array.isArray(accounts) || !hostname) return unscoped;

    const host = hostname.toLowerCase();
    const marketplaceIds = new Set(accounts.map(marketplaceIdOf).filter(Boolean));
    // A single-marketplace user — almost everyone — is never touched.
    if (marketplaceIds.size < 2) return unscoped;

    const matching = new Set();
    for (const account of accounts) {
        const baseAppHost = hostOf(account?.marketPlace?.baseAppURL);
        if (baseAppHost && hostsMatch(baseAppHost, host)) matching.add(marketplaceIdOf(account));
    }
    // No match: the user has no account in this domain's marketplace, so keep today's behavior.
    // Several: ambiguous, so don't guess.
    if (matching.size !== 1) return unscoped;

    const [marketplaceId] = matching;
    return {
        accounts: accounts.filter((account) => marketplaceIdOf(account) === marketplaceId),
        marketplaceId,
    };
};
